import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * The single canonical page heading for the product. Replaces the ad-hoc <h1>s
 * and the older AdminPageHeader / PageShell headers so every page shares one
 * type scale and colour. Uses design tokens (not shadcn) for text colour.
 */
const titleVariants = cva(
  "font-semibold tracking-tight text-(--color-text-primary) flex items-center gap-2",
  {
    variants: {
      size: {
        sm: "text-2xl",
        default: "text-3xl",
        lg: "text-4xl",
      },
    },
    defaultVariants: { size: "default" },
  },
);

export interface PageTitleProps extends VariantProps<typeof titleVariants> {
  title: ReactNode;
  description?: ReactNode;
  icon?: LucideIcon;
  iconClassName?: string;
  actions?: ReactNode;
  className?: string;
}

export function PageTitle({
  title,
  description,
  icon: Icon,
  iconClassName,
  actions,
  size,
  className,
}: PageTitleProps) {
  return (
    <div className={cn("flex items-center justify-between gap-4", className)}>
      <div className="min-w-0">
        <h1 className={titleVariants({ size })}>
          {Icon ? (
            <Icon className={iconClassName ?? "h-7 w-7 shrink-0"} />
          ) : null}
          <span className="truncate">{title}</span>
        </h1>
        {description ? (
          <p className="mt-1 text-sm text-(--color-text-secondary)">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
}
